import { Badge } from '@vrplatform/components';
import { formatCurrency, hasValue, sortBy } from '@vrplatform/utils';
import { type MouseEventHandler, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { LedgerStatementRow } from '../statement-list/statements-table/useStatementListTableQuery';
import { StatementDraftIcon, StatementPublishedIcon } from './_icons';
import { Icon } from '@vrplatform/icons';

export const LedgerStatementPaymentBadge = ({
  currency,
  row,
}: {
  currency: string | undefined;
  row: LedgerStatementRow;
}) => {
  const goto = useNavigate();

  const paidOut = row.payment.received;

  const sortedPayouts = useMemo(
    () => sortBy(row.payouts ?? [], 'date', 'asc'),
    [row.payouts]
  );

  const type = useMemo<
    'unpaid' | 'paid' | 'overpaid' | 'not-fully-paid'
  >(() => {
    if (row.payment.status === 'underpaid') return 'not-fully-paid';

    return row.payment.status;
  }, [row.payment.status]);

  const onClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();

      const search = new URLSearchParams();

      if (sortedPayouts.length) {
        if (sortedPayouts.length === 1) {
          search.set('date', sortedPayouts[0].date);
        } else {
          search.set(
            'date',
            [sortedPayouts.at(0)?.date, sortedPayouts.at(-1)?.date]
              .filter(hasValue)
              .join('...')
          );
        }
      }

      if (row.listing.id) search.set('listingIds', row.listing.id);
      goto(`/statements/payouts?${search.toString()}`);
    },
    [goto, row.listing.id, sortedPayouts]
  );

  return (
    <Badge
      component="button"
      tooltip={
        type === 'paid'
          ? 'Paid'
          : type === 'overpaid'
            ? 'Overpaid'
            : type === 'not-fully-paid'
              ? 'Not fully'
              : 'Unpaid'
      }
      onClick={onClick}
      disabled={sortedPayouts.length === 0}
      leftIcon={
        type === 'paid' ? (
          <StatementPublishedIcon size="xs" />
        ) : type === 'unpaid' ? (
          <UnpaidIcon size="xs" />
        ) : type === 'overpaid' ? (
          <Icon
            icon="CircleDollarIcon"
            size={12}
            color={(theme) => theme.colors.orange[8]}
          />
        ) : (
          <StatementDraftIcon size="xs" />
        )
      }
      sx={(theme) =>
        type === 'unpaid'
          ? {
              color: theme.colors.neutral[4],
            }
          : {}
      }
      color={
        type === 'paid'
          ? 'green'
          : type === 'overpaid'
            ? 'orange'
            : type === 'not-fully-paid'
              ? 'yellow'
              : 'gray'
      }
    >
      {formatCurrency(paidOut / 100, currency)}
    </Badge>
  );
};

const UnpaidIcon = ({ size }: { size: 'xs' | 'sm' }) => (
  <svg
    width={size === 'xs' ? 14 : 16}
    height={size === 'xs' ? 14 : 16}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="7.00002"
      cy="7.0013"
      r="5.83333"
      stroke="#D4D4D4"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="1 2"
    />
  </svg>
);
